import { useMutation, useQuery } from "@apollo/client";
import { Card } from "@blueprintjs/core";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import {
  CreateCustomerOrderInput,
  CustomerOrder,
  CustomerOrder_State,
  CustomerOrder_Type,
  User,
} from "../../../../api/schema";
import { FlexBoxInline, FlexContainer } from "../../../../components/flex/Flex";
import { ModalInputType } from "../../../../components/form/Form";
import {
  InputFormMode,
  ValuesVariables,
} from "../../../../components/form/InputForm";
import {
  FormInputFields,
  InputFormBuilder,
  InputFormBuilderState,
} from "../../../../components/form/InputFormBuilder";
import { Spinner } from "../../../../components/Spinner";
import { UserLanguage } from "../../../../lang/lang";
import { ClerkPrettyRenderer } from "../../../../utils/PrettyRenderer";
import {
  CreateCustomerOrderMutation,
  CreateCustomerOrderViewQuery,
} from "../graphql";
import {
  IconMapperState,
  IconMapperStateIntent,
  IconMapperType,
  IconMapperTypeIntent,
} from "../helper";
import "../style.css";

const OrderViewKeys = (bundle: {
  users: User[];
}): FormInputFields<CustomerOrder> => ({
  customer_contact: {
    type: ModalInputType.String,
    label: UserLanguage().data.customerOrder.customerContact,
  },
  customer_name: {
    type: ModalInputType.String,
    label: UserLanguage().data.customerOrder.customerName,
  },
  customer_reference: {
    type: ModalInputType.String,
    label: UserLanguage().data.customerOrder.customerReference,
  },
  delivery_date: {
    type: ModalInputType.Date,
  },
  notes: {
    type: ModalInputType.Text,
    label: UserLanguage().data.customerOrder.notes,
  },
  positions: {
    type: ModalInputType.OrderPositions,
    label: UserLanguage().data.customerOrder.positions,
  },
  state: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.customerOrder.state,
    icon: (i, r) => IconMapperState[i],
    intent: (i) => IconMapperStateIntent[i],
    items: _.values(CustomerOrder_State),
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.customerOrder.state
    ),
    defaultValue: CustomerOrder_State.accepted,
    translate: UserLanguage().data.customerOrder.states,
  },
  type: {
    type: ModalInputType.ItemSelect,
    icon: (i, r) => IconMapperType[i],
    intent: (i) => IconMapperTypeIntent[i],
    items: _.values(CustomerOrder_Type),
    label: UserLanguage().data.customerOrder.type,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.customerOrder.type
    ),
    defaultValue: CustomerOrder_Type.repair,
    translate: UserLanguage().data.customerOrder.types,
  },
  express: {
    type: ModalInputType.CheckBox,
    label: UserLanguage().data.customerOrder.express,
  },
  clerk: {
    type: ModalInputType.ItemSelect,
    items: bundle.users,
    icon: "user",
    label: UserLanguage().data.customerOrder.clerk,
    itemKey: "username",
    textRenderer: (c) => ClerkPrettyRenderer(c), // TODO: Better text!
  },
});

export const CreateCustomerOrderViewController = (props: {}) => {
  const { loading, error, data, refetch } = useQuery<{
    users: User[];
  }>(CreateCustomerOrderViewQuery);

  if (loading) return <Spinner />;
  if (data && data.users !== null)
    return (
      <CreateCustomerOrderView users={data.users} refetch={() => refetch()} />
    );

  console.log("got error", error);

  return <p>Error!</p>;
};

export const CreateCustomerOrderView = (props: {
  users: User[];
  refetch: () => void;
}) => {
  const [createCustomerOrderMutation] = useMutation<
    { createCustomerOrder: CustomerOrder },
    ValuesVariables<CreateCustomerOrderInput>
  >(CreateCustomerOrderMutation);

  const history = useHistory();

  return (
    <FlexContainer>
      <Card elevation={3}>
        <InputFormBuilder<
          CustomerOrder,
          { createCustomerOrder: CustomerOrder },
          CreateCustomerOrderInput
        >
          mode={InputFormMode.Create}
          onSuccess={(co) =>
            history.push("/customer/order/" + co.createCustomerOrder.id)
          }
          reformId={["clerk"]}
          stringyfy={[]}
          mutation={createCustomerOrderMutation}
          keys={OrderViewKeys({ users: props.users })}
          texts={UserLanguage().pages.customerOrder.actions}
        >
          {(builder: InputFormBuilderState<CustomerOrder>) => (
            <div>
              <div className="cow_header">
                <h2>
                  {UserLanguage().pages.customerOrder.actions.create.title}
                </h2>
              </div>
              <div className="cow_form">
                <div className="cow_form_col">
                  {builder.buildCards()}
                  <FlexBoxInline style={{ gap: 15 }}>
                    {builder.buildField("type")}
                    {builder.buildField("state")}
                  </FlexBoxInline>
                  {builder.buildField("clerk")}
                  {builder.buildField("customer_name")}
                  {builder.buildField("customer_contact")}
                  {builder.buildField("notes")}
                  {builder.buildSubmitButton()}
                </div>
                <div className="cow_form_col">
                  {builder.buildField("express")}
                  {builder.buildField("delivery_date")}
                </div>
              </div>
            </div>
          )}
        </InputFormBuilder>
      </Card>
    </FlexContainer>
  );
};
